import React, { Suspense } from "react";
import { Box, Image } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useReducedMotion } from "framer-motion";
import { useLandingLanguage } from "../../LandingLanguageContext";
import { MARKETING_LOCALES } from "../../../../marketing-content/marketingLocales";
import { useLandingTheme } from "../../LandingThemeContext";
import { DappledLight } from "../../visuals/DappledLight";
import { PointerParallax } from "../../visuals/Tilt3D";
import { JungleHeroContent } from "./JungleHeroContent";
// Only the Riverbank preview design shows the river; the Rive runtime it needs (and its WebAssembly) is
// fetched when that design is picked rather than shipped with Clearing, the production design.
const RiverBand = React.lazy(() => import("./RiverBand").then((module) => ({ default: module.RiverBand })));

export type LightJungleHeroVariant = "clearing" | "canopy" | "riverbank" | "diorama";

const INVENTORY = "/images/landing/inventory";

// A slow breeze: the fauna leans a degree or two and back.
const sway = keyframes`
  0%, 100% { transform: rotate(var(--sway-base)) translateY(0); }
  50% { transform: rotate(calc(var(--sway-base) + 1.6deg)) translateY(-6px); }
`;

// Clearing: the fauna strips reach in from both edges of a pale page. They are a designed pair:
// fauna-decor-2 is cut flat on its left (a left-edge strip) and fauna-decor on its right (a right-edge
// strip), so each sits in its natural orientation with the cut off-page. The strips pivot from the
// page-edge corner so the sway never swings a cut edge into view. (`mirror` flips the image itself,
// since the sway animation owns the wrapper's transform.)
//
// Each strip's soft shadow is a layer of its own behind the image rather than a drop-shadow filter on it
// (or on the wrapper): the browser counts a filter's blur as part of the image's painted area, which made
// the 20px of leaves showing at the edge of a phone, plus 72px of their shadow, the hero's largest
// contentful paint, timed by the SVG's download instead of by the headline. The layer draws exactly what
// drop-shadow(0 18px 24px rgba(20, 40, 16, 0.18)) drew: the image's silhouette (a mask) in the shadow
// color, 18px lower, blurred with the same 12px standard deviation. The blur sits on an outer box because
// a filter is applied before the same element's mask. `crossOrigin` makes the <img> request identical to
// the mask's CORS request, so the file downloads once.
function ClearingBackdrop({ animate }: { animate: boolean }) {
  const strip = (
    file: string,
    extra: React.ComponentProps<typeof Box>,
    base: string,
    duration: string,
    options: { mirror?: boolean; origin: string; hiddenOnPhones?: boolean }
  ) => {
    const source = `${INVENTORY}/${file}`;
    const mirrorTransform = options.mirror ? "scaleX(-1)" : undefined;
    return (
      <Box
        aria-hidden="true"
        position="absolute"
        pointerEvents="none"
        transformOrigin={options.origin}
        style={{ ["--sway-base" as string]: base }}
        transform={`rotate(${base})`}
        animation={animate ? `${sway} ${duration} ease-in-out infinite` : "none"}
        {...extra}
      >
        <Box position="absolute" left={0} right={0} top="18px" bottom="-18px" zIndex={-1} filter="blur(12px)">
          <Box
            h="100%"
            bg="rgba(20, 40, 16, 0.18)"
            transform={mirrorTransform}
            sx={{ maskImage: `url(${source})`, maskSize: "100% auto", maskRepeat: "no-repeat", maskPosition: "0 0" }}
          />
        </Box>
        {/* A lazy image inside `display: none` is not downloaded, so phones skip the strips they never show
            (neither is its mask, which is not drawn). */}
        <Image src={source} crossOrigin="anonymous" loading={options.hiddenOnPhones ? "lazy" : undefined} alt="" w="100%" transform={mirrorTransform} />
      </Box>
    );
  };
  return (
    <>
      {strip(
        "fauna-decor-2.svg",
        {
          left: { base: "-150px", lg: "-270px", xl: "-190px", "2xl": "-150px" },
          top: { base: "40px", lg: "-40px" },
          w: { base: "170px", lg: "280px" },
        },
        "-1.6deg",
        "7s",
        { origin: "left bottom" }
      )}
      {strip(
        "fauna-decor.svg",
        {
          right: { base: "-140px", lg: "-160px", xl: "-60px" },
          top: { base: "120px", lg: "60px" },
          w: { base: "200px", lg: "330px" },
          display: { base: "none", md: "block" },
        },
        "0deg",
        "8.5s",
        { origin: "right bottom", hiddenOnPhones: true }
      )}
      {/* Ferns at the lower left. Mirrored so this strip's cut side faces the page edge; the -20° lean
          then swings that edge further off-page in the part that shows above the fold. */}
      {strip(
        "fauna-decor.svg",
        {
          left: { base: "-60px", lg: "4px" },
          bottom: "-440px",
          w: { base: "160px", lg: "200px" },
          display: { base: "none", md: "block" },
        },
        "-20deg",
        "9s",
        { mirror: true, origin: "center", hiddenOnPhones: true }
      )}
      {/* The anthurium, lifted out of the flower strip (whose own copy sits off-page on the right). It sits
          over the one leaf in the left strip that the artist cropped with a straight edge. */}
      {strip(
        "anthurium.svg",
        {
          // On a phone the upload button runs almost edge to edge, so most of the flower stays off-page.
          left: { base: "-76px", lg: "-104px", xl: "-25px", "2xl": "6px" },
          top: { base: "292px", lg: "296px" },
          w: { base: "80px", lg: "110px" },
          zIndex: 1,
        },
        "-8deg",
        "6s",
        { origin: "center bottom" }
      )}
    </>
  );
}

// Canopy: sun through leaves on a pale wall — a generated photograph of leaf shadows on plaster
// (Gemini image model), tinted toward the page green, with the moving dapple on top.
function CanopyBackdrop({ animate }: { animate: boolean }) {
  return (
    <>
      <Box
        aria-hidden="true"
        position="absolute"
        inset={0}
        bgImage="url(/images/landing/generated/canopy-wall.jpg)"
        bgSize="cover"
        bgPosition="center"
      />
      <Box aria-hidden="true" position="absolute" inset={0} bg="rgba(237, 242, 230, 0.42)" />
      <Box
        aria-hidden="true"
        position="absolute"
        inset={0}
        bgImage="radial-gradient(ellipse 60% 50% at 82% 8%, rgba(255, 232, 150, 0.4), transparent 70%)"
      />
      <DappledLight mode="shadow" animate={animate} opacity={0.22} />
    </>
  );
}

// Riverbank: a pale sky, and the app's animated river as the ground the hero stands on.
function RiverbankBackdrop() {
  return (
    <>
      <Box
        aria-hidden="true"
        position="absolute"
        inset={0}
        bgImage="linear-gradient(180deg, #F3F7EC 0%, #EAF2F3 55%, #DDEBE3 100%)"
      />
      <Suspense fallback={null}>
        <RiverBand
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          h={{ base: "300px", md: "460px", lg: "600px" }}
          pointerEvents="none"
          overflow="hidden"
        />
      </Suspense>
      {/* Fade the scene's top edge into the sky so it reads as a horizon, not a strip. */}
      <Box
        aria-hidden="true"
        position="absolute"
        left={0}
        right={0}
        bottom={{ base: "220px", md: "380px", lg: "510px" }}
        h="90px"
        bgImage="linear-gradient(to bottom, #EAF2F3, rgba(234, 242, 243, 0))"
      />
    </>
  );
}

// Diorama: the app's layered leaves, hills and sun on separate depths that shift with the pointer.
function DioramaBackdrop() {
  return (
    <>
      <Box
        aria-hidden="true"
        position="absolute"
        inset={0}
        bgImage={`url(${INVENTORY}/IndividualTreeBackground.svg)`}
        bgSize="cover"
        bgPosition="center bottom"
        opacity={0.55}
      />
      <Box
        aria-hidden="true"
        position="absolute"
        inset={0}
        bgImage="linear-gradient(180deg, rgba(243, 247, 236, 0.55) 0%, rgba(243, 247, 236, 0.1) 60%, rgba(243, 247, 236, 0) 100%)"
      />
      <PointerParallax
        depth={-14}
        position="absolute"
        left={{ base: "10%", lg: "26%" }}
        right={{ base: "10%", lg: "26%" }}
        top={{ base: "-40px", lg: "-34px" }}
        pointerEvents="none"
        aria-hidden="true"
      >
        <Image
          src={`${INVENTORY}/TopLayeredLeavesBackground.svg`}
          alt=""
          w="100%"
          filter="drop-shadow(0 14px 18px rgba(20, 40, 16, 0.18))"
        />
      </PointerParallax>
      <PointerParallax
        depth={-26}
        position="absolute"
        left={{ base: "-80px", lg: "-30px" }}
        top="120px"
        w={{ base: "150px", lg: "240px" }}
        pointerEvents="none"
        aria-hidden="true"
      >
        <Image
          src={`${INVENTORY}/LeftLayeredLeavesBackground.svg`}
          alt=""
          w="100%"
          filter="drop-shadow(0 18px 22px rgba(20, 40, 16, 0.2))"
        />
      </PointerParallax>
      <PointerParallax
        depth={-30}
        position="absolute"
        right={{ base: "-90px", lg: "-40px" }}
        top="80px"
        w={{ base: "170px", lg: "270px" }}
        pointerEvents="none"
        aria-hidden="true"
      >
        <Image
          src={`${INVENTORY}/RightLayeredLeavesBackground.svg`}
          alt=""
          w="100%"
          filter="drop-shadow(0 18px 22px rgba(20, 40, 16, 0.2))"
        />
      </PointerParallax>
      <PointerParallax
        depth={18}
        position="absolute"
        left="-4%"
        right="-4%"
        bottom="-40px"
        pointerEvents="none"
        aria-hidden="true"
      >
        <Image src={`${INVENTORY}/IndividualTreeForeground.svg`} alt="" w="100%" display="block" />
      </PointerParallax>
    </>
  );
}

// The Moss page with a light hero. Same content and layout as Moss; only the backdrop differs by variant.
export function LightJungleHero({ variant }: { variant: LightJungleHeroVariant }) {
  const theme = useLandingTheme();
  const language = useLandingLanguage();
  const reduceMotion = useReducedMotion();
  const animate = !reduceMotion;
  const extraBottomPadding =
    variant === "riverbank"
      ? { base: "300px", md: "440px", lg: "540px" }
      : variant === "diorama"
        ? { base: "120px", lg: "180px" }
        : { base: "72px", lg: "112px" };
  return (
    <Box
      as="section"
      position="relative"
      overflow="hidden"
      // Runs up behind the transparent nav so the backdrop owns the whole fold.
      mt="-96px"
      pt={{ base: "128px", lg: "168px" }}
      pb={extraBottomPadding}
      minH={{ lg: "860px" }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      bg={theme.color.pageBg}
      zIndex={0}
    >
      {variant === "clearing" && (
        <Box position="absolute" inset={0} pointerEvents="none" transform={MARKETING_LOCALES[language].direction === "rtl" ? "scaleX(-1)" : undefined}>
          <ClearingBackdrop animate={animate} />
        </Box>
      )}
      {variant === "canopy" && <CanopyBackdrop animate={animate} />}
      {variant === "riverbank" && <RiverbankBackdrop />}
      {variant === "diorama" && <DioramaBackdrop />}
      <Box position="relative" zIndex={1} w="100%">
        <JungleHeroContent onDark={false} />
      </Box>
    </Box>
  );
}
